/**
 * This slice is dedicated to Market Data Service API data.
 * The data is currently consumed through Gateway.
 * TODO: To be deprecated in favour of Gateway. Currently direct
 *       MDS connection is used for debugging purposes only.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash-es';

import configLib from '../../../configLib';
import { CallStatus } from '../../enums';
import { GenericStatusByCall } from '../../enums/status-by-call';
import { CompanyLogoAndNameResponse } from '../../models/fundamental/types';
import { OHLCV } from '../../models/market-data/types';
import { StockDescriptor } from '../../models/stock-descriptor';
import { LoggerService } from '../../services/logger/Logger.service';
import { AppStateStatus } from '../../store-util/AppStateCache';
import {
  DisconnectedPayload, ErrorPayloadFull,
  loginFailed,
  logout,
  marketDataDisconnected,
  processSymbolSubscribesAction,
  processSymbolUnSubscribesAction,
  setAppStatus,
} from '../common-actions';

import { setMarketDataCallStatus } from './helpers';
import {
  GetCustomWatchlistDataPayload,
  GetWatchlistDataPayload, MarketDataCall,
  MarketDataQueryPayload,
  ProcessSymbolSubscribesPayload,
  ProcessSymbolUnSubscribesPayload,
  SymbolLite,
  UserFavoriteStocks,
} from './types';

const { base64 } = configLib;
const logger = LoggerService.getLogger();

const normalizeFavoriteSymbol = (symbol: string) => symbol.trim().toUpperCase();

const getFavoriteSymbolsFromPayload = (symbolOrSymbols: string): string[] => {
  const uniqueSymbols = new Set(
    symbolOrSymbols
      .split(',')
      .map(normalizeFavoriteSymbol)
      .filter(Boolean),
  );

  return Array.from(uniqueSymbols);
};

const normalizeFavoriteStocks = (favoriteStocks: SymbolLite[] = []): SymbolLite[] => {
  const uniqueSymbols = new Set<string>();

  favoriteStocks.forEach(item => {
    const symbol = normalizeFavoriteSymbol(item?.symbol ?? '');
    if (symbol) {
      uniqueSymbols.add(symbol);
    }
  });

  return Array.from(uniqueSymbols).map(symbol => ({ symbol }));
};

const getFavoriteStocksFromPreferenceData = (
  favoritePreferenceData: SymbolLite[] | { favouriteStocks?: SymbolLite[] },
) => {
  if (Array.isArray(favoritePreferenceData)) {
    return favoritePreferenceData;
  }

  return favoritePreferenceData?.favouriteStocks ?? [];
};

export type MarketDataState = {
  connected: boolean;
  favouriteStocks: UserFavoriteStocks;
  companies: {[key: string]: string}; // TODO: Remove this quickfix for reloading watchlist
  stockHistory: OHLCV[];
  stockRecent: OHLCV[];
  /**
   * For reference of Market State times see:
   *  (1) Google Presentation - https://docs.google.com/presentation/d/17j2erZF0NBqA67IF71b8ub0JK5p7-__OPhYCpyMRd00/edit#slide=id.g10947438435_0_36
   *  (2) Library constants at `capital-library/constants/marketHours.ts`
   */
  openPositionsSubscribed: boolean;
  isInitialPriceDataLoaded: boolean;
  statusByCall: GenericStatusByCall<MarketDataCall>;
}
export const INITIAL_STATE: MarketDataState = {
  connected: false,
  favouriteStocks: {},
  companies: {}, // TODO: Remove this quickfix for reloading watchlist
  stockHistory: [],
  stockRecent: [],
  openPositionsSubscribed: false,
  /**
   * Marks if the main groups of symbols have price data loaded:
   *  1. WatchList (popular & favorites)
   *  2. Discover (top gainers, top losers, new on the market)
   *  3. My Account (my stocks - open positions)
   */
  isInitialPriceDataLoaded: false,
  statusByCall: {},
};

const marketDataSlice = createSlice({
  name: 'marketData',
  initialState: INITIAL_STATE,
  reducers: {
    sendFavoriteStocksToServer(state, action) {
      setMarketDataCallStatus(MarketDataCall.sendFavoriteStocksToServer, CallStatus.PENDING, state);
    },
    sendFavoriteStocksToServerCompleted(state, action) {
      setMarketDataCallStatus(MarketDataCall.sendFavoriteStocksToServer, CallStatus.READY, state);
    },
    sendFavoriteStocksToServerFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setMarketDataCallStatus(MarketDataCall.sendFavoriteStocksToServer, CallStatus.ERROR, state, status);
    },
    retrieveFavoriteStocksFromServer(state) {
      setMarketDataCallStatus(MarketDataCall.getFavoriteStocksFromServer, CallStatus.PENDING, state);
    },
    retrieveFavoriteStocksFromServerCompleted(state, action) {
      const { data, customData } = action.payload;
      const { userId } = customData;

      try {
        const favoriteStocks = getFavoriteStocksFromPreferenceData(JSON.parse(base64.decode(data)));
        state.favouriteStocks[userId] = isEmpty(favoriteStocks) ? [] : normalizeFavoriteStocks(favoriteStocks);
      } catch (error) {
        logger.warn('can not parse response to json! error:', error);
        state.favouriteStocks[userId] = [];
      }

      setMarketDataCallStatus(MarketDataCall.getFavoriteStocksFromServer, CallStatus.READY, state);
    },
    retrieveFavoriteStocksFromServerFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setMarketDataCallStatus(MarketDataCall.getFavoriteStocksFromServer, CallStatus.ERROR, state, status);
    },
    connected(state) { state.connected = true; },
    /** Dispatched only by InitialSymbolsDataLoadingStatusCache.updateStatus */
    confirmInitialPriceDataLoaded(state) {
      state.isInitialPriceDataLoaded = true;
    },
    getWatchlistData(state, action: PayloadAction<GetWatchlistDataPayload>) {},
    getCustomWatchlistData(state, action: PayloadAction<GetCustomWatchlistDataPayload>) {},
    stockHistoryLoaded(state, action) {
      state.stockHistory = action.payload;
    },
    stockRecentLoaded(state, action) {
      state.stockRecent = action.payload;
    },
    marketDataError(state, action) {
      logger.error(action.payload);
    },
    addCompanyNameToCache(state: MarketDataState, action: PayloadAction<StockDescriptor>) {
      const { symbol, companyName } = action.payload;

      if (companyName && symbol && !state.companies[symbol]) {
        state.companies[symbol] = companyName;
      } else if (!state.companies[symbol]) {
        console.warn('[market-data/index]: could not add company to cache - case 1, addCompanyNameToCache', action.payload);
      }
    },
    /**
     * The payload of the action has `symbols` - this is one symbol or comma separated list as string
     */
    addCompanyToCache(state: MarketDataState, action: PayloadAction<{
      responseData: CompanyLogoAndNameResponse[], symbols: string
    }>) {
      const companies = action.payload.responseData ?? [];
      const { symbols } = action.payload;

      if (companies.length > 0) {
        const symbolsArray: string[] = symbols.split(',');

        symbolsArray.forEach(item => {
          const { symbol, name } = companies.find(c => c.symbol === item) || {};

          if (symbol && name && !state.companies[item]) {
            state.companies[symbol] = name;
          } else if (!state.companies[symbol!]) {
            console.warn('[market-data/index] addCompanyToCache - could not add company to cache', { symbol, name });
          }
        });
      }
    },
    /** Used only by loadSymbolDataForInitialListsEpic */
    setInitialSymbolDataLoaded(state: MarketDataState) {
      state.isInitialPriceDataLoaded = true;
    },
    getMarketDataQuery(state: MarketDataState, action: PayloadAction<MarketDataQueryPayload>) {},
  },
  extraReducers: {
    [setAppStatus.type]: (
      state,
      action: PayloadAction<AppStateStatus>,
    ) => {
      if (action.payload === 'background') {
        state.isInitialPriceDataLoaded = false;
      }
    },
    [loginFailed.type]: state => { state.isInitialPriceDataLoaded = false; },
    [marketDataDisconnected.type]: (
      state,
      action: PayloadAction<DisconnectedPayload>,
    ) => {
      state.connected = false;
      if (action.payload && action.payload?.message) {
        logger.error(action.payload.message);
      }
    },
    [logout.type]: state => ({ ...INITIAL_STATE, favouriteStocks: state.favouriteStocks }),
    [processSymbolSubscribesAction.type]: (
      state,
      action: PayloadAction<ProcessSymbolSubscribesPayload>,
    ) => {
      const { symbolOrSymbols, isFavorites, userCorrelationId } = action.payload;
      if (!isFavorites || !userCorrelationId) {
        return;
      }

      const symbols = getFavoriteSymbolsFromPayload(symbolOrSymbols);
      if (!symbols.length) {
        return;
      }

      const favouriteStocks = normalizeFavoriteStocks(state.favouriteStocks[userCorrelationId]);
      const favouriteSymbols = new Set(favouriteStocks.map(item => item.symbol));
      symbols.forEach(symbol => {
        if (!favouriteSymbols.has(symbol)) {
          favouriteStocks.push({ symbol });
          favouriteSymbols.add(symbol);
        }
      });
      state.favouriteStocks[userCorrelationId] = favouriteStocks;
    },
    [processSymbolUnSubscribesAction.type]: (
      state,
      action: PayloadAction<ProcessSymbolUnSubscribesPayload>,
    ) => {
      const { symbolOrSymbols, isRemovingFromFavorites, userCorrelationId } = action.payload;
      if (!isRemovingFromFavorites || !userCorrelationId) {
        return;
      }

      const symbols = new Set(getFavoriteSymbolsFromPayload(symbolOrSymbols));
      if (!symbols.size) {
        return;
      }

      const favouriteStock = normalizeFavoriteStocks(state.favouriteStocks[userCorrelationId]);
      state.favouriteStocks[userCorrelationId] = favouriteStock
        .filter(item => !symbols.has(item.symbol));
    },
  },
});

export const {
  connected,
  confirmInitialPriceDataLoaded,
  getMarketDataQuery,
  getWatchlistData,
  getCustomWatchlistData,
  stockHistoryLoaded,
  stockRecentLoaded,
  marketDataError,
  addCompanyNameToCache,
  addCompanyToCache,
  setInitialSymbolDataLoaded,
  sendFavoriteStocksToServer,
  sendFavoriteStocksToServerCompleted,
  sendFavoriteStocksToServerFailed,
  retrieveFavoriteStocksFromServer,
  retrieveFavoriteStocksFromServerCompleted,
  retrieveFavoriteStocksFromServerFailed,
} = marketDataSlice.actions;

export default marketDataSlice.reducer;
